import { MonitorDown, Monitor, Moon, Sun } from 'lucide-react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { changeLanguage, LANGUAGES, type LanguageCode } from '@/i18n';
import { useInstallPrompt } from '@/lib/pwa';
import { useTheme } from '@/lib/theme';
import { cn } from '@/lib/cn';
import { HomeLogo, Logo } from './Logo';
import { PrivacyLedger } from './PrivacyLedger';
import { Button, IconButton, Segmented } from './primitives';

export function LanguageSwitch() {
  const { t, i18n } = useTranslation();
  return (
    <div className="w-[92px]">
      <Segmented<LanguageCode>
        size="sm"
        label={t('nav.language')}
        value={i18n.language as LanguageCode}
        onChange={changeLanguage}
        options={LANGUAGES.map((language) => ({ value: language.code, label: language.short, hint: language.label }))}
      />
    </div>
  );
}

export function ThemeToggle() {
  const { t } = useTranslation();
  const { theme, cycle } = useTheme();
  const icon = theme === 'dark' ? <Moon size={17} /> : theme === 'light' ? <Sun size={17} /> : <Monitor size={17} />;
  const label = theme === 'dark' ? t('nav.themeDark') : theme === 'light' ? t('nav.themeLight') : t('nav.themeSystem');
  return (
    <IconButton label={label} onClick={cycle}>
      {icon}
    </IconButton>
  );
}

/** "Install app" — only shown where the browser supports installing. */
function InstallButton() {
  const { t } = useTranslation();
  const install = useInstallPrompt();
  if (!install) return null;
  return (
    <Button variant="ghost" size="sm" icon={<MonitorDown size={15} />} aria-label={t('nav.install')} onClick={() => void install()}>
      <span className="hidden sm:inline">{t('nav.install')}</span>
    </Button>
  );
}

export interface AppHeaderProps {
  readonly left?: ReactNode;
  readonly right?: ReactNode;
  readonly compact?: boolean;
  /**
   * A page rather than a tool: the bar's content lines up with the page column
   * (as on the content pages) and the logo leads home.
   */
  readonly page?: boolean;
}

export function AppHeader({ left, right, compact = false, page = false }: AppHeaderProps) {
  return (
    <header className="shrink-0 border-b border-line">
      <div className={cn('flex h-16 items-center gap-2 sm:gap-3', page ? 'mx-auto w-full max-w-300 px-5 sm:px-10 lg:px-18' : 'px-3 sm:px-5')}>
        <div className="flex min-w-0 flex-1 items-center gap-4">{left ?? (page ? <HomeLogo /> : <Logo />)}</div>
        {/* Everything on the right keeps its size; the left side gives way on narrow phones. */}
        <div className="flex shrink-0 items-center gap-1 sm:gap-2">
          {!compact && <InstallButton />}
          {/* In the editor a narrow phone needs the room for undo and export; the home page still shows it. */}
          <div className={compact ? 'hidden min-[400px]:block' : undefined}>
            <PrivacyLedger compact={compact} />
          </div>
          {right}
          {/* In the editor, phone space goes to undo and export instead. */}
          <div className={compact ? 'hidden sm:block' : undefined}>
            <LanguageSwitch />
          </div>
          <div className={compact ? 'hidden sm:block' : undefined}>
            <ThemeToggle />
          </div>
        </div>
      </div>
    </header>
  );
}
